import axios from "axios";
import bearerAxios from "../helpers/bearerAxios";
import { logger } from "./loggingService";
import { useViewerStore } from "../store/store";
import { DomesticContentResponse, EquipmentItem, EquipmentOptions, ExcelCellData, FinancePartnerDetails, InverterCalculatorData, RackingQuantitiesResponse, RoofProductionInformation } from "../@types/types";
import { GetRoundNum } from "../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/Common";
import { EquipmentType } from "../@types/enums";
import { emptyPackingSlipRows } from "../Constants";


const fetchEqipmentOptions = async (finPartnerId: number): Promise<EquipmentOptions | null> => {

    const data = {
        "environment": import.meta.env.VITE_EXCEL_ENV_NAME,
        "financePartnerId": finPartnerId.toString(),
        "state": sessionStorage.getItem('stateCode'),
    };

    const token = await getExcelServiceAPIToken();
    return await axios({
        method: 'post',
        url: `${import.meta.env.VITE_EXCELSERVICES_API_URL}`,
        data: {
            "ExcelFileName": "Net Suite Equipment Calculator",
            "SheetName": "DC Evaluation IO V3",
            "InputValues": [{ "CellNumber": "C3", "CellValue": data, "Type": "String" }],
            "OutputCells": ["E5", "E6", "E7", "E8"],
        },
        headers: { Authorization: `Bearer ${token}` }
    })
        .then((res) => {
            let equipmentOptions: EquipmentOptions = {}
            if (res.status === 200) {

                const getCellValue = (cellNumber: string) => res.data.OutputValues.find((o: any) => o.CellNumber === cellNumber)?.CellValue;
                // equipmentOptions = JSON.parse(res.data.OutputValues[0].CellValue).equipmentOptions;
                equipmentOptions = {
                    modules: JSON.parse(getCellValue('E5'))?.modules,
                    inverters: JSON.parse(getCellValue('E6'))?.inverters,
                    optimizers: JSON.parse(getCellValue('E7'))?.optimizers,
                    rackingSystems: JSON.parse(getCellValue('E8'))?.rackingSystems,
                }
            }
            return equipmentOptions;
        })
        .catch((err) => {
            logger.error(err);
            return null
        })
}

const getDefaultValues = async (data: InverterCalculatorData) => {

    const result: any = {};

    const financeParnter = data.financePartnerDetails.name;
    // Refer sheet 'igsEquipmentV3' for IGS configuration
    if (financeParnter === 'IGS') {
        const excelResults = await fetchExcelIgsInverters(data);
        if (!excelResults?.length) return { error: 'No default equipments loaded from inverter calculator' };
        // const optimizerModelId = excelResults[0].optimizerModel;
        // const optimizerMasterData = data.eqOptions?.inverters?.find(e => e.financePartnerModelId === optimizerModelId);
        result[EquipmentType.Optimizer] = excelResults[0].optimizerSalesforceListing;
        result[EquipmentType.RackingSystem] = excelResults[0].rackingSalesforceListing;
        result[EquipmentType.Rail] = excelResults[0].rackingRails[0];
        // result[EquipmentType.Fastener] = excelResults[0].rackingFasteners;

        let inverters: EquipmentItem[] = [];

        excelResults.forEach(array => {
            const existingIdx = inverters.findIndex(inv => inv.financePartnerEquipmentId === array.inverterId);

            if (existingIdx === -1) {
                const masterData = data.eqOptions?.inverters?.find(e => e.financePartnerModelId === array.inverterId);
                if (!masterData) return;
                const inverter = {
                    category: EquipmentType.Inverter,
                    name: `Inverter${inverters.length === 0 ? '' : inverters.length + 1}`,
                    model: masterData?.model,
                    financePartnerEquipmentId: masterData?.financePartnerModelId,
                    quantity: array.inverterQuantity,
                    roofNumbers: [Number(array.roofId)],
                }
                inverters.push(inverter);
            } else {
                inverters[existingIdx] = {
                    ...inverters[existingIdx],
                    quantity: (inverters[existingIdx].quantity ?? 0) + array.inverterQuantity,
                    roofNumbers: [...(inverters[existingIdx].roofNumbers ?? []), Number(array.roofId)]
                }
            }
        })
        result[EquipmentType.Inverter] = inverters;

    } else {
        // Refer sheet 'Inverter Configurator' for configuration other than IGS
        const excelRowResult = await fetchExcelBatteryInverters(data);
        if (excelRowResult) {
            result.error = excelRowResult.find(row => row.CellNumber === 'X5')?.CellValue ?? 'No default equipments loaded from inverter calculator';
            const inverterRowCount = Number(excelRowResult.find(row => row.CellNumber === 'P2')?.CellValue) || 0;
            if (inverterRowCount) {
                const excelResults = await fetchExcelBatteryInverters(data, false, inverterRowCount);
                result.error = excelResults.find(row => row.CellNumber === 'X5')?.CellValue ?? 'No default equipments loaded from inverter calculator';

                result[EquipmentType.Optimizer] = excelResults.find(row => row.CellNumber === 'V5')?.CellValue;
                result[EquipmentType.RackingSystem] = excelResults.find(row => row.CellNumber === 'T5')?.CellValue;

                let inverters: EquipmentItem[] = [];
                for (let i = 0; i <= inverterRowCount; i++) {
                    const array = excelResults.find(row => row.CellNumber === `I${5 + i}`)?.CellValue;
                    const roofNum = data.roofInfoArr.filter(r => r.modules > 0)[Number(array) - 1]?.roofNum ?? Number(array);
                    const model = excelResults.find(row => row.CellNumber === `L${5 + i}`)?.CellValue;
                    const quantity = excelResults.find(row => row.CellNumber === `N${5 + i}`)?.CellValue ?? 0;
                    const existingIdx = inverters.findIndex(inv => inv.model === model);

                    if (existingIdx === -1) {
                        const masterData = data.eqOptions?.inverters?.find(e => e.model === model);
                        if (!masterData) continue;
                        const inverter: EquipmentItem = {
                            category: EquipmentType.Inverter,
                            name: `Inverter${inverters.length === 0 ? '' : inverters.length + 1}`,
                            model,
                            financePartnerEquipmentId: masterData?.financePartnerModelId,
                            manufacturer: masterData?.manufacturer,
                            quantity: Number(quantity),
                            roofNumbers: array !== undefined ? [roofNum] : []
                        }
                        inverters.push(inverter);
                    } else {
                        const oldRoofs = inverters[existingIdx].roofNumbers ?? [], isAdded = oldRoofs.includes(roofNum);
                        if (!isAdded) {
                            inverters[existingIdx] = {
                                ...inverters[existingIdx],
                                quantity: (inverters[existingIdx].quantity ?? 0) + Number(quantity),
                                roofNumbers: [...oldRoofs, roofNum]
                            }
                        }
                    }
                }
                result[EquipmentType.Inverter] = inverters;
            }
        }
    }


    return result;
}


const fetchExcelBatteryInverters = async (data: InverterCalculatorData, isCheckingRowCount = true, inverterRowCount = 1): Promise<ExcelCellData[]> => {
    const token = await getExcelServiceAPIToken();
    const { activeModule, moduleCount, arrayCount, financePartnerDetails } = data;

    const stateCode = sessionStorage.getItem('stateCode');

    const getOutputCells = () => {
        const inverterColumns = ["I", "J", "K", "L", "M", "N"]; //"O", "P", "Q", "R", "S", "T", "U", "V"
        const outputCells = ['S5', 'T5', 'U5', 'V5', 'V9', 'X5'];

        // Getting the list of output cells based on the design array count from the previous excel call
        for (let i = 0; i <= inverterRowCount; i++) {
            inverterColumns.forEach(c => outputCells.push(`${c}${i + 5}`));
        }
        return outputCells;
    }


    return await axios({
        method: 'post',
        url: `${import.meta.env.VITE_EXCELSERVICES_API_URL}`,
        data: {
            "ExcelFileName": "Battery Inverter Calculator",
            "SheetName": "Inverter Configurator",
            "InputValues": [
                { "CellNumber": "B3", "CellValue": stateCode, "Type": "String" },
                { "CellNumber": "C3", "CellValue": financePartnerDetails.productType, "Type": "String" }, //prodType
                { "CellNumber": "D3", "CellValue": activeModule.text, "Type": "String" },
                { "CellNumber": "E3", "CellValue": moduleCount, "Type": "String" },
                { "CellNumber": "G3", "CellValue": "", "Type": "String" }, // battery Qty
                { "CellNumber": "B6", "CellValue": import.meta.env.VITE_EXCEL_ENV_NAME, "Type": "String" }, // Environment
                { "CellNumber": "G6", "CellValue": arrayCount, "Type": "String" }, // Design Array Count
                { "CellNumber": "B9", "CellValue": financePartnerDetails.name, "Type": "String" },
                { "CellNumber": "C9", "CellValue": financePartnerDetails.purchaseMethod, "Type": "String" }, //financeMethod
                { "CellNumber": "D9", "CellValue": "", "Type": "String" }, // Battery Finance Name
                { "CellNumber": "E9", "CellValue": "", "Type": "String" }, // Battery Finance Method
                { "CellNumber": "G9", "CellValue": "Common", "Type": "String" }, // Ground Mount = "Common"/ "Roof Mount"
                { "CellNumber": "B12", "CellValue": financePartnerDetails.roughInspection, "Type": "String" }, //roughInspection
                { "CellNumber": "C12", "CellValue": financePartnerDetails.ahjTown, "Type": "String" },
            ],
            OutputCells: isCheckingRowCount ? ["P2", "F9", "X5"] : getOutputCells(),
        },
        headers: { Authorization: `Bearer ${token}` }
    })
        .then((res) => {
            if (res.status === 200) {
                return res.data.OutputValues as ExcelCellData[];
            }
            return [];
        })
        .catch((err) => {
            logger.error(err);
            return []
        })
}


const fetchExcelIgsInverters = async (data: InverterCalculatorData): Promise<any[] | null> => {
    const token = await getExcelServiceAPIToken();
    const { activeModule, moduleCount, arrayCount, financePartnerDetails, roofInfoArr } = data;
    const stateCode = sessionStorage.getItem('stateCode');
    const jsonObj = {
        state: stateCode,
        product: financePartnerDetails.productType,
        moduleType: activeModule.text,
        systemSize: (moduleCount * activeModule.power) / 1000, // In kWh
        solarFinancePartner: financePartnerDetails.name,
        // mountingType: "Ground Mount", //"Ground Mount" : "Roof Mount";
        solarFinanceMethod: financePartnerDetails.purchaseMethod,
        roughInspectionRequired: financePartnerDetails.roughInspection ?? 'No',
        township: financePartnerDetails.ahjTown,
        numberOfArrays: arrayCount,
        arrayDetails: roofInfoArr.filter(array => array.modules > 0).map(array => ({
            arrayId: array.roofNum,
            tilt: array.pitch,
            orientation: GetRoundNum(array.azimuth),
            unshadedPercent: array.solarAccess,
            efficiency: array.efficiency,
            tof: array.solarAccess, // 2,
            moduleCount: Number(array.modules),
            annualProduction: GetRoundNum(Number(array.actual_production)),
        }))
    }

    return await axios({
        method: 'post',
        url: `${import.meta.env.VITE_EXCELSERVICES_API_URL}`,
        data: {
            "ExcelFileName": "Battery Inverter Calculator",
            "SheetName": "igsEquipmentV3",
            "InputValues": [
                { "CellNumber": "B3", "CellValue": jsonObj, "Type": "String" },
            ],
            OutputCells: ["C3", "H3"],
        },
        headers: { Authorization: `Bearer ${token}` }
    })
        .then((res) => {
            if (res.status === 200) {
                const outputValues = res.data.OutputValues as ExcelCellData[];
                const UIdata = outputValues.find(d => d.CellNumber === 'H3')?.CellValue;
                return UIdata ? JSON.parse(UIdata).equipment : null;
            }
            return null;
        })
        .catch((err) => {
            logger.error(err);
            return null
        })
}


const fetchDomesticContent = async (data: any): Promise<DomesticContentResponse | null> => {

    const token = await getExcelServiceAPIToken();

    const response = await axios({
        method: 'post',
        url: `${import.meta.env.VITE_EXCELSERVICES_API_URL}`,
        data: {
            "ExcelFileName": "Net Suite Equipment Calculator",
            "SheetName": "DC Evaluation IO V3",
            "InputValues": [
                {
                    "CellNumber": "C4",
                    "CellValue": data,
                    "Type": "string"
                }
            ],
            "OutputCells": ["E3", "E4"],
        },
        headers: { Authorization: `Bearer ${token}` }
    });

    if (response.status === 200) {
        const result = JSON.parse(response.data.OutputValues[1].CellValue).evaluationResult;
        const domesticResult: DomesticContentResponse = {
            ...result,
            systemIsDomestic: result.systemIsDomestic === 'Yes',
            systemDCPercentage: GetRoundNum(result.systemDCPercentage * 100, 2),
        }
        return domesticResult;
    }
    return null;
}

async function fetchFinancePartnerDetails(): Promise<FinancePartnerDetails> {
    const opId = sessionStorage.getItem('SalesForceId');
    const response = await bearerAxios.get(
        `${import.meta.env.VITE_ONEBUTTON_CONTRACT_BASE_URL + import.meta.env.VITE_FETCH_FINANCE_PARTNER_DETAILS!}?opportunityId=${opId}`,
        { headers: { "Content-Type": "application/json" } }
    );

    if (response.status === 200) {
        const partnerList = response.data;
        const solarPartner = partnerList.find((p: any) => p.Product_Type_Name === "Solar");
        const contractId = solarPartner ? solarPartner.Contract_Id : partnerList?.[0]?.Contract_Id ?? '';
        // An opportunity with no Solar partner reports none: a fabricated default
        // shows up downstream as the "current" partner and hides the real gap.
        return {
            name: solarPartner?.Finance_Partner_Display_Name ?? '',
            id: solarPartner?.Finance_Partner_Id ?? 0,
            ahjTown: solarPartner?.AuthorityAHJTownship,
            purchaseMethod: solarPartner?.Purchase_Method,
            roughInspection: solarPartner?.Rough_Inspection,
            productType: 'S',
            contractId: contractId,
        };
    }

    return { name: '', id: 0, productType: 'S' };
}

async function savePackingSlipData(payload: any) {

    try {
        const response = await bearerAxios.post(
            import.meta.env.VITE_BASE_URL + import.meta.env.VITE_SAVE_PACKING_SLIP_VALUES!,
            payload,
            { headers: { "Content-Type": "application/json" } }
        );

        const success = response.status === 200;
        const { FileName, URL } = response.data.BoxResponse;

        return { success, FileName, URL };
    } catch (error) {
        logger.error(error);
    }

    return { success: false, FileName: '', URL: '' };
}

async function fetchPackingSlipData(designId?: number): Promise<{ equipments: EquipmentItem[], boxURL: string } | null> {
    if (!designId) return null;
    const opId = sessionStorage.getItem('SalesForceId');

    try {
        const response = await bearerAxios.get(
            `${import.meta.env.VITE_BASE_URL + import.meta.env.VITE_FETCH_PACKING_SLIP_VALUES!}?SFOpportunityId=${opId}&DesignId=${designId}`,
            { headers: { "Content-Type": "application/json" } }
        );
        const getFastenerCategory = (name: string): EquipmentType | undefined =>
            emptyPackingSlipRows.find(row => row.name === name)?.category;

        if (response.data && response.data.Equipments) {
            const equipments = response.data.Equipments.map((item: any) => ({
                category: item.Category === EquipmentType.Fastener ? getFastenerCategory(item.Name) : item.Category,
                name: item.Name,
                model: item.Model,
                quantity: item.Quantity,
                roofNumbers: item.Array_Number,
                manufacturer: item.Manufacturer,
                financePartnerEquipmentId: item.FinancePartnerEquipmentId,
            }));
            const boxURL = response.data.Box_URL;
            return { equipments, boxURL };
        } else {
            return null;
        }
    } catch (error) {
        logger.error(error);
    }
    return null;
}

const generatePackingSlipFile = async (designId: number) => {
    const opportunityId = sessionStorage.getItem('SalesForceId');
    const payload = { opportunityId, designId };
    try {
        const res = await bearerAxios.post(
            `${import.meta.env.VITE_BASE_URL + import.meta.env.VITE_GENERATE_PACKING_SLIP_VALUES}`,
            payload,
            { headers: { "Content-Type": "application/json" } },
        );
        return res.status === 200;
    } catch (err) {
        return err
    }
}

async function getRackingCalculatorInput(designId: number): Promise<string> {
    const oppId = sessionStorage.getItem('SalesForceId');
    try {
        const response = await bearerAxios.get(
            `${import.meta.env.VITE_BASE_URL}/api/Modules/${oppId}/Design/${designId}`,
            { headers: { "Content-Type": "application/json" } }
        );

        if (response.status === 200) {
            const backendData = response.data;

            // Validate if no rows in response
            if (!backendData.Rows || Object.keys(backendData.Rows).length === 0) {
                throw new Error('No quantities available in the database for this design');
            }

            if (!backendData.ArrayCount || !backendData.ModuleCount || !backendData.RowCount) {
                throw new Error('Invalid design data: missing array/module/row counts');
            }

            // Transform to Excel JSON string format
            const cellValue = JSON.stringify({
                arrayCount: backendData.ArrayCount,
                moduleCount: backendData.ModuleCount,
                rowCount: backendData.RowCount,
                rows: backendData.Rows
            });
            return cellValue;
        }
        throw new Error('Backend API failed');
    } catch (error) {
        logger.error(error);
        throw error;
    }
}

async function calculateRackingQuantities(designId: number): Promise<RackingQuantitiesResponse> {
    try {
        // Load input cellValue from backend
        const cellValue = await getRackingCalculatorInput(designId);
        const token = await getExcelServiceAPIToken();

        const res = await axios({
            method: "post",
            url: `${import.meta.env.VITE_EXCELSERVICES_API_URL}`,
            data: {
                "ExcelFileName": "Racking BOM Calculator",
                "SheetName": "RBC IO",
                "InputValues": [
                    { "CellNumber": "C3", "CellValue": cellValue, "Type": "String" },
                ],
                OutputCells: ["E3"],
            },
            headers: { Authorization: `Bearer ${token}` },
        });

        if (res.status !== 200 || !res.data.OutputValues) {
            console.error('Excel service failed:', res.status);
            return {};
        }
        const outputCell = res.data.OutputValues?.find(
            (c: any) => c.CellNumber === "E3",
        );
        if (!outputCell?.CellValue) {
            console.error('No E3 output from Excel');
            return {};
        }

        const parsed = JSON.parse(outputCell.CellValue) as any;
        return {
            rail: parsed.output.railCount,
            splice: parsed.output.spliceCount,
            midClamp: parsed.output.midClampCount,
            endClamp: parsed.output.endClampCount,
            mlpeBracket: parsed.output.mlpeBracketCount,
        };
    } catch (err) {
        logger.error(err);
        return {};
    }
}

const getSavePayload = (equipmentList?: EquipmentItem[]) => {
    const { selectedDesignIndex, designList, financePartnerDetails, activeModule, modeCount } = useViewerStore.getState();
    const activeDesign = designList && designList[selectedDesignIndex];
    const opId = sessionStorage.getItem('SalesForceId');
    // const equipments = equipmentList.length ? equipmentList.map((eq, idx) => ({ ...eq, id: idx })) :
    const equipments = (equipmentList && equipmentList.length) ? equipmentList : [
        {
            category: EquipmentType.Module,
            name: 'Module',
            model: activeModule.text,
            quantity: modeCount,
            manufacturer: activeModule.manufacturer,
        }
    ];
    const data = {
        sF_OpportunityId: opId,
        design_Id: activeDesign?.DesignID ?? null,
        finance_Partner: financePartnerDetails?.name?.trim() || 'Good Leap',
        equipments,
    };
    return data;
}

const getExcelServiceAPIToken = async () => {
    try {
        const res = await axios.get(import.meta.env.VITE_EXCELSERVICES_TOKEN_API_URL!)
        return res.data.token;
    } catch (err) {
        return err
    }
}

const packingSlipService = {
    savePackingSlipData,
    getDefaultValues,
    generatePackingSlipFile,
    fetchPackingSlipData,
    fetchDomesticContent,
    fetchEqipmentOptions,
    fetchFinancePartnerDetails,
    getSavePayload,
    calculateRackingQuantities,
}

export default packingSlipService